import React from "react";

import type { PreparedPoint } from "../types";
import type { OverlayInteractionEvent } from "../types";
import { pointStyle } from "../styles";

interface PointRendererProps {
  annotation: PreparedPoint;
  x: number;
  y: number;
  onInteraction: (event: OverlayInteractionEvent) => void;
}

/**
 * Renders a single point annotation as a small dot centered at (x, y) in
 * display-pixel space. Interaction uses a slightly larger transparent hit
 * area so hover/click feel natural.
 */
export const PointRenderer: React.FC<PointRendererProps> = React.memo(({ annotation, x, y, onInteraction }) => {
  const style = pointStyle;
  const { size, hitPadding } = style;
  const hoverId = annotation.interactionId ?? annotation.id;
  const clickId = annotation.id;

  const dotStyle: React.CSSProperties = {
    position: "absolute",
    left: 0,
    top: 0,
    width: size,
    height: size,
    borderRadius: "50%",
    backgroundColor: style.fill,
    borderStyle: style.borderStyle,
    borderWidth: style.borderWidth,
    borderColor: style.border,
    boxSizing: "border-box",
    pointerEvents: "auto",
    cursor: "pointer",
    transform: `translate(${x - size / 2}px, ${y - size / 2}px)`,
  };

  const hitStyle: React.CSSProperties = {
    position: "absolute",
    left: 0,
    top: 0,
    width: size + hitPadding * 2,
    height: size + hitPadding * 2,
    transform: `translate(${x - size / 2 - hitPadding}px, ${y - size / 2 - hitPadding}px)`,
    pointerEvents: "auto",
    cursor: "pointer",
    background: "transparent",
  };

  return (
    <>
      <div data-annotation-id={clickId} style={dotStyle} />
      <div
        data-annotation-hit={clickId}
        style={hitStyle}
        onClick={() => onInteraction({ type: "click", annotationId: clickId })}
        onMouseEnter={() => onInteraction({ type: "mouseenter", annotationId: hoverId })}
        onMouseLeave={() => onInteraction({ type: "mouseleave", annotationId: hoverId })}
      />
    </>
  );
});

PointRenderer.displayName = "PointRenderer";
